import { createBrowserRouter } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardPage } from '@/pages/DashboardPage';
import { LoginPage } from '@/pages/LoginPage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { GapAnalysisPage } from '@/features/alm/GapAnalysisPage';
import { NimAnalysisPage } from '@/features/nim/NimAnalysisPage';
import { UsersPage } from '@/features/users/UsersPage';
import { ProfilePage } from '@/features/users/ProfilePage';
import { PermissionsPage } from '@/features/permissions/PermissionsPage';
import { LiquidityGapReportPage } from '@/features/reporting/LiquidityGapReportPage';
import { RateGapReportPage } from '@/features/reporting/RateGapReportPage';
import { LcrNsfrReportPage } from '@/features/reporting/LcrNsfrReportPage';
import { FlowModelsPage } from '@/features/parameters/FlowModelsPage';
import { FlowModelEditorPage } from '@/features/parameters/FlowModelEditorPage';
import { AffectationsPage } from '@/features/parameters/AffectationsPage';
import { PreviewPage } from '@/features/parameters/PreviewPage';
import { AlmParametersPage } from '@/features/parameters/AlmParametersPage';
import { YieldCurvesPage } from '@/features/parameters/YieldCurvesPage';
import { DurationAnalysisPage } from '@/features/duration/DurationAnalysisPage';
import { EveAnalysisPage } from '@/features/eve/EveAnalysisPage';
import { LiquidityAnalysisPage } from '@/features/liquidity/LiquidityAnalysisPage';
import { FtpAnalysisPage } from '@/features/ftp/FtpAnalysisPage';
import IrrbbReportPage from '@/features/reporting/IrrbbReportPage';
import { StressReportPage } from '@/features/reporting/StressReportPage';
import { StressScenariosPage } from '@/features/stress/StressScenariosPage';
import { DataUploadPage }             from '@/features/data/DataUploadPage';
import { AccountingBalancesPage }     from '@/features/data/AccountingBalancesPage';
import { AlertsPage }            from '@/features/alerts/AlertsPage';
import { PositionImportPage }    from '@/features/positionImport/PositionImportPage';
import { ReportConfigsPage }         from '@/features/parameters/ReportConfigsPage';
import { MatrixSegmentationsPage }  from '@/features/parameters/MatrixSegmentationsPage';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      {
        path: 'alm/gaps',
        element: (
          <ProtectedRoute permission="gap:read">
            <GapAnalysisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'alm/liquidity',
        element: (
          <ProtectedRoute permission="lcr:read">
            <LiquidityAnalysisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'alm/ftp',
        element: (
          <ProtectedRoute permission="gap:read">
            <FtpAnalysisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'alm/nim',
        element: (
          <ProtectedRoute permission="gap:read">
            <NimAnalysisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'alm/duration',
        element: (
          <ProtectedRoute permission="gap:read">
            <DurationAnalysisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'alm/eve',
        element: (
          <ProtectedRoute permission="gap:read">
            <EveAnalysisPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'alm/stress',
        element: (
          <ProtectedRoute permission="gap:read">
            <StressScenariosPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reporting',
        element: (
          <PlaceholderPage
            title="Reporting"
            description="Pick a report from the sidebar — Liquidity gap, Rate gap, or LCR/NSFR."
          />
        ),
      },
      {
        path: 'reporting/gap-liquidity',
        element: (
          <ProtectedRoute permission="gap:read">
            <LiquidityGapReportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reporting/gap-rate',
        element: (
          <ProtectedRoute permission="gap:read">
            <RateGapReportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reporting/lcr',
        element: (
          <ProtectedRoute permission="lcr:read">
            <LcrNsfrReportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reporting/irrbb',
        element: (
          <ProtectedRoute permission="gap:read">
            <IrrbbReportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'reporting/stress/:runId',
        element: (
          <ProtectedRoute permission="gap:read">
            <StressReportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'data/upload',
        element: (
          <ProtectedRoute permission="gap:read">
            <DataUploadPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'data/accounting-balances',
        element: (
          <ProtectedRoute permission="gap:read">
            <AccountingBalancesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'data/import',
        element: (
          <ProtectedRoute permission="gap:read">
            <PositionImportPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'market',
        element: (
          <PlaceholderPage
            title="Market Data"
            description="Curves, FX rates, and benchmarks consumed by cx-market."
          />
        ),
      },
      {
        path: 'parameters/segmentations',
        element: (
          <ProtectedRoute permission="gap:read">
            <MatrixSegmentationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'parameters/flow-models',
        element: (
          <ProtectedRoute permission="gap:read">
            <FlowModelsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'parameters/flow-models/new',
        element: (
          <ProtectedRoute permission="gap:read">
            <FlowModelEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'parameters/flow-models/:id',
        element: (
          <ProtectedRoute permission="gap:read">
            <FlowModelEditorPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'parameters/affectations',
        element: (
          <ProtectedRoute permission="gap:read">
            <AffectationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'parameters/preview',
        element: (
          <ProtectedRoute permission="gap:read">
            <PreviewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'parameters/yield-curves',
        element: (
          <ProtectedRoute permission="gap:read">
            <YieldCurvesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <PlaceholderPage
            title="Settings"
            description="Tenant configuration, calculation parameters, user roles."
          />
        ),
      },
      {
        path: 'settings/profile',
        element: <ProfilePage />,
      },
      {
        path: 'settings/users',
        element: (
          <ProtectedRoute permission="user:admin">
            <UsersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/alm-parameters',
        element: (
          <ProtectedRoute permission="gap:read">
            <AlmParametersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/report-configs',
        element: (
          <ProtectedRoute permission="gap:read">
            <ReportConfigsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/permissions',
        element: (
          <ProtectedRoute permission="gap:read">
            <PermissionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'alm/alerts',
        element: (
          <ProtectedRoute permission="gap:read">
            <AlertsPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
